import React, { useState } from 'react';
import { Button, CircularProgress, Alert, Snackbar } from '@mui/material';
import { Save } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useMsal, useAccount } from '@azure/msal-react';
import { useAppSelector } from '../../../../../app/hooks';
import { useUpsertVendorMutation } from '../../../../../services/VendorService';

interface SaveVendorButtonProps {
  label?: string;
  successMessage?: string;
}

const SaveVendorButton: React.FC<SaveVendorButtonProps> = ({
  label = 'Save Vendor',
  successMessage = 'Vendor saved successfully!',
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const { currentVendor, isEdited } = useAppSelector(state => state.vendor);
  const [upsertVendor] = useUpsertVendorMutation();
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});

  const validateRequiredFields = (): boolean => {
    if (!currentVendor) return false;

    const missingFields: string[] = [];

    if (!currentVendor.companyName?.trim()) missingFields.push('Company Name');
    if (!currentVendor.status?.trim()) missingFields.push('Status');

    if (missingFields.length > 0) {
      setValidationMessage(`Please complete the following required fields: ${missingFields.join(', ')}`);
      setShowValidationError(true);
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!currentVendor || !isEdited) return;

    if (!validateRequiredFields()) return;

    setIsSaving(true);
    try {
      const userName = account?.name || 'Unknown User';
      const isNewVendor = !currentVendor.id || currentVendor.id === '';

      const result = await upsertVendor({
        vendorView: currentVendor,
        modifiedBy: userName,
      }).unwrap();

      setShowSuccess(true);

      if (isNewVendor && result.id) {
        setTimeout(() => navigate(`/vendors/edit?id=${result.id}`, { replace: true }), 1500);
      }
    } catch (error) {
      console.error('Failed to save vendor:', error);
      setValidationMessage('Failed to save vendor. Please try again.');
      setShowValidationError(true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={handleSave}
        disabled={!isEdited || isSaving}
        startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save />}
        sx={{
          backgroundColor: '#162346',
          color: 'white',
          fontWeight: 600,
          '&:hover': {
            backgroundColor: '#1a2a52',
          },
          '&:disabled': {
            backgroundColor: '#64748b',
            color: '#ffffff',
          },
        }}
      >
        {isSaving ? 'Saving...' : label}
      </Button>

      <Snackbar
        open={showValidationError}
        autoHideDuration={6000}
        onClose={() => setShowValidationError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowValidationError(false)} severity="error" sx={{ width: '100%' }}>
          {validationMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success" variant="filled">
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SaveVendorButton;
